import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import { useComparison } from '@context/CompareContext';
import { useLocale } from '@hooks/useLocale';
import SimpleButton from '_components/UI/SimpleButton';
import TextButton from '_components/UI/TextButton';
import CompareBarCard from './CompareBarCard';

import styles from './CompareBar.module.scss';

/** Products required before the comparison table is worth opening. */
const MIN_COMPARE_ITEMS = 2;

/**
 * Positions the product row is padded out to with placeholder slots, so a
 * near-empty tray still fills the bar. Purely presentational — the tray itself
 * is unbounded, and past this many products there are simply no placeholders.
 */
const FILLED_ROW_LENGTH = 4;

/**
 * Sticky tray listing the products selected for comparison. Appears as soon as
 * the first product is added and stays put while the shopper keeps browsing.
 *
 * Figma: Compare Bar 747:356759 (desktop) / 747:357862 (mobile)
 */
const CompareBar = () => {
  const { t } = useTranslation('comparison');
  const locale = useLocale();
  const router = useRouter();
  const { items, pending, allItems, clearItems } = useComparison();

  const selectedCount = allItems.length;
  const isOpen = selectedCount > 0;
  const canCompare = selectedCount >= MIN_COMPARE_ITEMS;

  // Resolved products first, then a skeleton per product still being fetched.
  const resolvedItems = useMemo(
    () =>
      items.filter(
        (item) => item.code !== undefined && !pending.includes(item.code)
      ),
    [items, pending]
  );

  const placeholderCount = Math.max(0, FILLED_ROW_LENGTH - selectedCount);

  const titleValues = { selected: selectedCount };

  const handleCompare = () => {
    if (!canCompare) {
      return;
    }
    router.push(`/${locale}/compare`);
  };

  return (
    <section
      // Redundant alongside the labelled <section>, but ED-193 asks for it.
      role="region"
      aria-label={t('bar.aria.label')}
      className={cn(styles.bar, { [styles.open]: isOpen })}
      data-testid="compare-bar"
    >
      <div className={styles.titleRow}>
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={styles.title}
        >
          {/*
            Both strings render so the breakpoint swap stays in CSS. The short
            one is hidden from assistive tech, leaving exactly one live region
            that always announces the full label.
          */}
          <span className={styles.titleLong}>
            {t('bar.title', titleValues)}
          </span>
          <span className={styles.titleShort} aria-hidden="true">
            {t('bar.title.short', titleValues)}
          </span>
        </p>

        <div className={styles.actions}>
          <SimpleButton
            className={styles.clearAll}
            onClick={() => clearItems()}
          >
            {t('bar.clear.all')}
          </SimpleButton>
          {/*
            aria-disabled rather than the native disabled attribute: ED-193 also
            requires every control to stay reachable by Tab.
          */}
          <TextButton
            className={styles.compareButton}
            label={t('bar.compare')}
            aria-disabled={!canCompare}
            onClick={handleCompare}
            data-testid="compare-bar-compare"
          />
        </div>
      </div>

      <div className={styles.productList}>
        {resolvedItems.map((item) => (
          <CompareBarCard key={item.code} product={item} />
        ))}
        {pending.map((code) => (
          <CompareBarCard key={code} />
        ))}
        {Array.from({ length: placeholderCount }, (_, index) => (
          <div
            // Decorative filler; the count is already announced by the title.
            aria-hidden="true"
            className={styles.placeholder}
            data-testid="compare-bar-placeholder"
            key={`compare-bar-placeholder-${index}`}
          />
        ))}
      </div>
    </section>
  );
};

export default CompareBar;
